import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FileCheck2, RefreshCw, Plus, Trash2, ExternalLink, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";

const VSTATUS = {
  verleend: "text-lime-400 border-lime-500/30", aangevraagd: "text-sky-400 border-sky-500/30",
  geweigerd: "text-rose-400 border-rose-500/30", ingetrokken: "text-slate-400 border-slate-600", onbekend: "text-slate-400 border-slate-600",
};

const AddForm = ({ aid, onDone }) => {
  const [f, setF] = useState({ titel: "", datum: new Date().toISOString().slice(0, 10), status: "verleend", kenmerk: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/addresses/${aid}/vergunningen`, f);
      toast.success("Vergunning toegevoegd");
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={submit} className="space-y-2 border border-slate-800 rounded-md p-3 mb-3" data-testid="permit-add-form">
      <Input required data-testid="permit-title-input" placeholder="Omschrijving, bv. 'plaatsen dakkapel voorzijde'" value={f.titel} onChange={(e) => setF({ ...f, titel: e.target.value })} className="h-9 bg-slate-950 border-slate-800 text-xs" />
      <div className="grid grid-cols-3 gap-2">
        <Input type="date" data-testid="permit-date-input" value={f.datum} onChange={(e) => setF({ ...f, datum: e.target.value })} className="h-9 bg-slate-950 border-slate-800 text-xs" />
        <select data-testid="permit-status-select" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="h-9 rounded-md bg-slate-950 border border-slate-800 px-2 text-xs">
          {["verleend", "aangevraagd", "geweigerd"].map((s) => <option key={s}>{s}</option>)}
        </select>
        <Input data-testid="permit-ref-input" placeholder="Kenmerk" value={f.kenmerk} onChange={(e) => setF({ ...f, kenmerk: e.target.value })} className="h-9 bg-slate-950 border-slate-800 text-xs" />
      </div>
      <button data-testid="permit-add-submit" className="w-full h-8 rounded-md bg-blue-600 hover:bg-blue-500 text-xs">Toevoegen</button>
    </form>
  );
};

export const VergunningenPanel = ({ addressId }) => {
  const qc = useQueryClient();
  const key = ["vergunningen", addressId];
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const { data = [] } = useQuery({ queryKey: key, queryFn: () => api.get(`/addresses/${addressId}/vergunningen`).then((r) => r.data) });
  const reload = () => qc.invalidateQueries({ queryKey: key });

  const sync = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post(`/addresses/${addressId}/vergunningen/sync`);
      qc.setQueryData(key, d);
      toast.success(`${d.length} vergunning(en)/bekendmaking(en) gevonden`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-slate-800 bg-[#111827]/80 rounded-lg p-5" data-testid="permits-panel">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-display font-semibold flex items-center gap-2"><FileCheck2 className="h-4 w-4" /> Vergunningen ({data.length})</h3>
        <div className="flex gap-1">
          <button data-testid="permits-sync-btn" onClick={sync} disabled={busy} title="Ophalen uit officiële bekendmakingen" className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </button>
          <button data-testid="permits-add-toggle" onClick={() => setAdding(!adding)} className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-white"><Plus className="h-4 w-4" /></button>
        </div>
      </div>
      <p className="text-[11px] text-slate-500 mb-3">Uit officielebekendmakingen.nl (gemeenteblad) en handmatig register. Nieuwe BAG-panden tellen ook als vergund.</p>
      {adding && <AddForm aid={addressId} onDone={() => { setAdding(false); reload(); }} />}
      {data.length === 0 && <p className="text-xs text-slate-500">Geen vergunningen bekend.</p>}
      <div className="space-y-2 max-h-72 overflow-y-auto">
        {data.map((v) => (
          <div key={v.id} className="text-xs border-b border-slate-800/60 pb-2" data-testid={`permit-item-${v.id}`}>
            <div className="flex items-center gap-2">
              <span className={`font-mono text-[10px] uppercase border rounded px-1.5 ${VSTATUS[v.status] || VSTATUS.onbekend}`}>{v.status}</span>
              <span className="font-mono text-slate-500">{v.datum}</span>
              <span className="text-[10px] text-slate-600 uppercase">{v.bron}</span>
              {v.bron === "handmatig" && (
                <button data-testid={`permit-delete-${v.id}`} onClick={async () => { await api.delete(`/vergunningen/${v.id}`); reload(); }} className="ml-auto text-slate-500 hover:text-rose-400"><Trash2 className="h-3 w-3" /></button>
              )}
            </div>
            <div className="text-slate-300 mt-1 leading-snug">
              {v.titel}
              {v.url && <a href={v.url} target="_blank" rel="noreferrer" className="inline-flex ml-1 text-blue-400"><ExternalLink className="h-3 w-3" /></a>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
